import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

import { Address, Alias, Verification } from '../types/customerProfile';

export function getDateFormatted(dateISOStr: Date | null | undefined) {
  if (dateISOStr) {
    const date = dayjs(dateISOStr, 'YYYY-MM-DD');
    return date.format('DD MMM YYYY');
  }
  return '';
}

export function getDateTimeFormatted(dateTimeISOStr: Date | null | undefined) {
  if (dateTimeISOStr) {
    const date = dayjs(dateTimeISOStr);
    return date.format('DD MMM YYYY');
  }
  return '';
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function dateToISO8601Timestamp(this: any, key: any): string {
  return dayjs(this).format();
}

// eslint-disable-next-line no-extend-native
Date.prototype.toJSON = dateToISO8601Timestamp;

export function getDisplayGender(gender: string) {
  switch (gender) {
    case 'F': {
      return 'Female';
    }
    case 'M': {
      return 'Male';
    }
    case 'U':
    case 'X': {
      return 'Undisclosed';
    }
    default: {
      return '';
    }
  }
}

export function isEmptyArray(array: Array<any>) {
  return array == null || array.length === 0;
}

export function isNil(str: string | undefined) {
  return str == null || str.trim().length === 0;
}

export function isEMEA(countryCode: string) {
  if (!countryCode) {
    return false;
  }
  return ['GB', 'ZA'].includes(countryCode.toUpperCase());
}

export function isGB(countryCode: string) {
  if (!countryCode) {
    return false;
  }
  return 'GB' === countryCode.toUpperCase();
}

export function isZA(countryCode: string) {
  if (!countryCode) {
    return false;
  }
  return 'ZA' === countryCode.toUpperCase();
}

export function isLscv(countryCode: string) {
  return isAUNZ(countryCode);
}

export function isAUNZ(countryCode: string) {
  if (!countryCode) {
    return false;
  }
  return ['AU', 'NZ'].includes(countryCode.toUpperCase());
}

export function isWhitelistedMpBrand(countryCode: string) {
  // This is a copy from @fclimited/gscv-lib-core. Can't pull this in unless we upgrade typescript & react scripts :(
  return ['FC', 'TM', 'CA', 'TA', 'LT', 'SL'].includes(countryCode);
}

export function getMpBrandLabel(brand: string | undefined | null) {
  if (!brand) {
    return '';
  }

  switch (brand) {
    case 'FC':
      return 'Flight Centre';
    case 'TM':
      return 'Travel Money';
    case 'CA':
      return 'Cruiseabout';
    case 'TA':
      return 'Travel Associates';
    case 'LT':
      return 'Liberty Travel';
    case 'SL':
      return 'Support Leisure';
    default:
      return '';
  }
}

export function isUSCA(countryCode: string) {
  if (!countryCode) {
    return false;
  }
  return ['US', 'CA'].includes(countryCode.toUpperCase());
}

export function filterMarketingPref(mp: any, countryCode: string, brand: string) {
  const brandMatch = mp.brand === brand;
  const countryMatch = mp.countryCode === countryCode;
  return brandMatch && countryMatch;
}

export function formatAddress(a: Address): string {
  return (
    a.addressLine1 +
    (isNil(a.addressLine2) ? '' : ' ' + a.addressLine2) +
    (isNil(a.addressLine3) ? '' : ' ' + a.addressLine3) +
    (isNil(a.city) ? '' : ', ' + a.city) +
    (isNil(a.state) ? '' : ', ' + a.state) +
    (isNil(a.postcode) ? (isNil(a.country) ? '' : ', ' + a.country) : ', ' + a.postcode + (isNil(a.country) ? '' : ' ' + a.country))
  );
}

export function getAliasProfileIdText(aliases: Alias[] | undefined): string[] {
  if (!aliases || aliases.length === 0) {
    return [];
  }

  return aliases
    .filter((alias) => alias.source !== 'MYACCT')
    .map((alias) => {
      return alias.source === 'LMS' ? 'W360 Rewards: ' + alias.sourceProfileId : alias.source + ' ID: ' + alias.sourceProfileId;
    });
}

export function getMyAccountAliasEmail(aliases: Alias[] | undefined): string | undefined {
  return aliases?.find((alias) => alias.source === 'MYACCT')?.sourceProfileId;
}

export function isStudentVerified(verifications: Verification[] | undefined): boolean {
  if (!verifications || !Array.isArray(verifications)) {
    return false;
  }

  return verifications.some((verification) => verification.type === 'student' && verification.status === 'success');
}

const validChar = "[A-Za-z\u00C0-\u017F][A-Za-z\u00C0-\u017F-'\\s]";
export const nameRegEx = 'matchRegexp:^' + validChar + '*$';
export const nameCanEndswithStarRegEx = 'matchRegexp:^' + validChar + '*\\*?$';
export const nameWildcardRegEx = 'matchRegexp:^' + validChar + '{2,}\\*?$';
export const profileIdRegex = 'matchRegexp:^([A-Za-z]{2}[-])*[A-Za-z0-9]{6}$'; // accounts for both fctgId & globalId
export const profileIdUSCARegex = 'matchRegexp:^([A-Za-z]{2}[-])*[A-Za-z0-9]{6}|\\d{9}$';
export const isGlobalId = (id: string) => /[A-Za-z]{2}-[A-Za-z0-9]{6}/.test(id); // e.g. 'NZ-0AABB1' returns true
